import { defineComponent as _defineComponent } from 'vue'
import { normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import type { PropType } from 'vue';
import type { SortByItem } from '@/models/llDataTableTypes';

// Reusable table shell shared across list pages (Matters is the first
// consumer; Fixed Fees/Time Entries can adopt it later without a rewrite).
// It owns only headers/items/loading/sort/selection wiring - column
// rendering, row actions, loading/empty states, and pagination placement
// all stay with the consuming page via slots and the separate
// LlDataTablePagination component, since those vary too much per page to
// bake into one shared shape.
//
// `fixed-header` is intentionally not a default here: it only has a visual
// effect when the table is also given a bounded height, which needs to be
// solved per-page against that page's actual layout (see MattersTable.vue's
// history with the app shell's real scroll container before reaching for
// this).
//
// This deliberately uses v-data-table-server, not plain v-data-table. Every
// consumer here does its own server-side sort/pagination, and plain
// v-data-table still re-sorts and re-paginates whatever `items` it's given
// on top of that (matching header.value against each item, client-side) -
// harmless for columns where the displayed field is also the sort key, but
// it silently produces the wrong order for any column where they differ
// (e.g. Matters' Responsible column sorts by initials server-side but
// displays/would compare full names client-side). v-data-table-server
// trusts `items`' given order completely and never re-sorts/re-paginates it.


export default /*@__PURE__*/_defineComponent({
  __name: 'LlDataTable',
  props: {
  headers: { type: Array, required: true },
  items: { type: Array, required: true },
  itemsLength: { type: [Number, String], required: true },
  loading: { type: Boolean, default: false },
  sortBy: { type: Array as PropType<SortByItem[]>, default: (): SortByItem[] => [] },
  itemValue: { type: String, default: 'id' },
  mustSort: { type: Boolean, default: true },
  showSelect: { type: Boolean, default: false },
  modelValue: { type: Array, default: undefined },
  // Colors the selection checkboxes (and selected-row tint) - defaults to the
  // theme's primary so every consumer matches without having to set it
  // themselves; override only if a specific page needs to differ. Vuetify
  // passes this to each checkbox explicitly, so the VCheckboxBtn default in
  // vuetifyTheme.ts never reaches them.
  color: { type: String, default: 'primary' },
  // Per-row attributes, as Vuetify's row-props: either an object applied to
  // every row, or a ({ item, index }) => object for row-dependent styling.
  // Pages that tint rows by state (Track Sent Emails shades paid invoices)
  // need this; without it they'd have to take over the whole `item` slot and
  // lose show-select's built-in selection handling.
  rowProps: { type: [Object, Function], default: undefined },
  // Item values of the currently expanded rows, paired with the `expanded-row`
  // slot. `showExpand` only controls whether Vuetify renders its own chevron
  // column - a page that drives expansion from its own cells (Track Sent
  // Emails toggles from the Type and Recipients cells, as it always has) binds
  // `expanded` and leaves `showExpand` off so the column set is unchanged.
  expanded: { type: Array, default: () => [] },
  showExpand: { type: Boolean, default: false },
},
  emits: ['update:sort-by', 'update:model-value', 'update:expanded'],
  setup(__props) {





return (_ctx: any,_cache: any) => {
  const _component_v_data_table_server = _resolveComponent("v-data-table-server")!

  return (_openBlock(), _createBlock(_component_v_data_table_server, {
    headers: __props.headers,
    items: __props.items,
    "items-length": __props.itemsLength,
    loading: __props.loading,
    "sort-by": __props.sortBy,
    "item-value": __props.itemValue,
    "show-select": __props.showSelect,
    "model-value": __props.modelValue,
    color: __props.color,
    "row-props": __props.rowProps,
    expanded: __props.expanded,
    "show-expand": __props.showExpand,
    density: "comfortable",
    hover: "",
    "hide-default-footer": "",
    "must-sort": __props.mustSort,
    "onUpdate:sortBy": _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('update:sort-by', $event))),
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('update:model-value', $event))),
    "onUpdate:expanded": _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('update:expanded', $event)))
  }, _createSlots({ _: 2 }, [
    _renderList(Object.keys(_ctx.$slots), (slotName) => {
      return {
        name: slotName,
        fn: _withCtx((scope) => [
          _renderSlot(_ctx.$slots, slotName, _normalizeProps(_guardReactiveProps(scope || {})))
        ])
      }
    })
  ]), 1032, ["headers", "items", "items-length", "loading", "sort-by", "item-value", "show-select", "model-value", "color", "row-props", "expanded", "show-expand", "must-sort"]))
}
}

})